<template>
  <div class="dish-or-ingredient">
    <div class="menu">
      <Tooltip v-if="extended && isDish" placement="bottom-end">
        <template #trigger>
          <IconButton class="menu-item" name="zoom_in_map" icon-size="22" @click="closeExtended" />
        </template>
        Zamknij widok rozszerzony
      </Tooltip>
      <Tooltip v-else-if="!extended && isDish" placement="bottom-end">
        <template #trigger>
          <IconButton class="menu-item" name="zoom_out_map" icon-size="22" @click="showExtened" />
        </template>
        Edycja zaawansowana i<br>
        wartości odżywcze
      </Tooltip>
      <Tooltip v-if="isDish" placement="bottom-end">
        <template #trigger>
          <IconButton
            class="menu-item"
            name="save"
            icon-size="22"
            :disabled="dishSaveInProgress"
            @click="saveDishHandler"
          />
        </template>
        Zapisz potrawę do<br>bazy prywatnej
      </Tooltip>
      <Tooltip v-if="canDietBeEdited" placement="bottom-end">
        <template #trigger>
          <IconButton
            class="menu-item"
            name="delete"
            icon-size="22"
            variant="outline"
            type="destructive"
            @click="deleteDishOrIngredientHandler"
          />
        </template>
        <template v-if="isDish">Usuń potrawę z jadłospisu</template>
        <template v-else>Usuń produkt z jadłospisu</template>
      </Tooltip>
      <Tooltip placement="bottom-end">
        <template #trigger>
          <IconButton
            class="menu-item"
            name="close"
            icon-size="22"
            @click="closeDishOrIngredientHandler"
          />
        </template>
        <template v-if="isDish">Zamknij widok potrawy</template>
        <template v-else>Zamknij widok produktu</template>
      </Tooltip>
    </div>
    <template v-if="dishOrIngredientCopy && dishOrIngredientCopy.type === 'dish'">
      <DietDishForm
        :key="refreshDishOrIngredient"
        :mode="'edit'"
        :extended="extended"
        :dish="dishOrIngredientCopy"
        :dish-index="dishIndex"
        :chosen-nutrients="chosenNutrients"
        :losses-multiplier="lossesMultiplier"
        @add-ingredient="addIngredient"
        @delete-ingredient="deleteIngredient"
        @replace-ingredient="replaceIngredient"
        @update-ingredient-in-dish-quantity="updateIngredientDishQuantity"
        @update-name="updateName"
        @update-quantity="updateQuantity"
        @update-size="updateSize"
        @update-used-portions="updateUsedPortions"
        @update-max-portions="updateMaxPortions"
        @update-preparation-time="updatePreparationTime"
        @update-preparation-steps="updatePreparationSteps"
        @update-meal-type="updateMealType"
        @update-dish-type="updateDishType"
        @update-dish-types="updateDishTypes"
      />
    </template>

    <template v-if="dishOrIngredientCopy && dishOrIngredientCopy.type === 'ingredient'">
      <DietIngredientForm
        :key="refreshDishOrIngredient"
        :extended="extended"
        :mode="'edit'"
        :ingredient="dishOrIngredientCopy"
        :chosen-nutrients="chosenNutrients"
        :losses-multiplier="lossesMultiplier"
        @update-quantity="updateQuantity"
        @update-size="updateSize"
      />
    </template>
  </div>
</template>

<script lang="ts" setup>
  import { A, D, F, O, pipe } from '@mobily/ts-belt'
  import { klona } from 'klona'
  import { storeToRefs } from 'pinia'
  import { computed, ref } from 'vue'

  import DietDishForm from '@/components/diets/DietDishForm.vue'
  import DietIngredientForm from '@/components/diets/DietIngredientForm.vue'
  import { IconButton } from '@/components/IconButton'
  import { Tooltip } from '@/components/ui'
  import { useDietsStore } from '@/store/dietsStore'
  import { useGlobalStore } from '@/store/globalStore'

  import type { DietIngredient, StoreDietDish } from '@/types/Diet'
  import type { Nutrients } from '@/utils/nutrients'

  type Data = {
    ingredient: DietIngredient
    ingredientIndex: number
  }

  const defaultNutrients: Nutrients[] = [
    'protein',
    'fat',
    'carbohydrates',
    'fiber',
    'carbohydrate_exchanger',
  ]

  const refreshDishOrIngredient = ref(0)
  const globalStore = useGlobalStore()
  const dietStore = useDietsStore()
  const { diet, details, extended, lossesMultiplier, dishSaveInProgress, canDietBeEdited } =
    storeToRefs(dietStore)

  const dishIndex = computed(() => {
    if (diet.value && details.value) {
      return `${details.value.dayIndex}-${details.value.mealIndex}-${details.value.dishOrIngredientIndex}`
    }
    return ''
  })
  const dishOrIngredient = computed(() => {
    if (diet.value && details.value) {
      const { mealIndex, dayIndex, dishOrIngredientIndex } = details.value

      return pipe(
        O.fromNullable(diet.value.data.meals[mealIndex]),
        O.flatMap(D.get('days')),
        O.flatMap(A.get(dayIndex)),
        O.flatMap(D.get('dishes_and_ingredients')),
        O.flatMap(A.get(dishOrIngredientIndex)),
        O.match(F.identity, () => {
          dietStore.updateDetails()
          return undefined
        }),
      )
    }
    return undefined
  })
  const dishOrIngredientCopy = computed(() => {
    return klona(dishOrIngredient.value)
  })
  const isDish = computed(() => {
    return dishOrIngredient.value?.type === 'dish'
  })
  const chosenNutrients = computed<Nutrients[]>(() => {
    if (globalStore.hasPerm('accounts.diet_microelements')) {
      return diet.value?.data.choosen_nutrients ?? defaultNutrients
    } else {
      return defaultNutrients
    }
  })

  const addIngredient = (ingredient: DietIngredient) => {
    if (details.value) {
      dietStore.addIngredientToDish({
        dayIndex: details.value.dayIndex,
        dishOrIngredientIndex: details.value.dishOrIngredientIndex,
        ingredient,
        mealIndex: details.value.mealIndex,
      })
    }
  }
  const replaceIngredient = (data: Data) => {
    if (details.value) {
      dietStore.replaceIngredientInDish({
        dayIndex: details.value.dayIndex,
        dishOrIngredientIndex: details.value.dishOrIngredientIndex,
        ingredient: data.ingredient,
        ingredientIndex: data.ingredientIndex,
        mealIndex: details.value.mealIndex,
      })
    }
  }
  const deleteIngredient = ({ originalIndex }: { originalIndex: number }) => {
    if (details.value) {
      dietStore.deleteIngredientInDish({
        dayIndex: details.value.dayIndex,
        dishOrIngredientIndex: details.value.dishOrIngredientIndex,
        ingredientIndex: originalIndex,
        mealIndex: details.value.mealIndex,
      })
    }
  }
  const updateIngredientDishQuantity = ({
    ingredientIndex,
    quantity,
  }: {
    ingredientIndex: number
    quantity: number
  }) => {
    if (details.value) {
      dietStore.updateIngredientInDishQuantity({
        dayIndex: details.value.dayIndex,
        dishOrIngredientIndex: details.value.dishOrIngredientIndex,
        ingredientIndex,
        mealIndex: details.value.mealIndex,
        quantity,
      })
    }
  }
  const saveDishHandler = () => {
    if (globalStore.hasAddDishPermission && dishOrIngredient.value?.type === 'dish') {
      const dish = dishOrIngredient.value
      if (dish.name) {
        dietStore.updpateDishSaveInProgressStatus(true)
        dietStore.debouncedSaveDish(dish)
      } else {
        globalStore.createMessage({
          title: 'Nazwa potrawy jest wymagana',
        })
      }
    } else {
      globalStore.showAppUpgradePlanModal()
    }
  }
  const updateName = (name: string) => {
    if (details.value) {
      dietStore.updateDishName({
        dayIndex: details.value.dayIndex,
        dishOrIngredientIndex: details.value.dishOrIngredientIndex,
        mealIndex: details.value.mealIndex,
        name,
      })
    }
  }
  const updateSize = (size: number) => {
    if (details.value) {
      dietStore.updateDishSize({
        dayIndex: details.value.dayIndex,
        dishOrIngredientIndex: details.value.dishOrIngredientIndex,
        mealIndex: details.value.mealIndex,
        size,
      })
    }
  }
  const updateQuantity = (quantity: number) => {
    if (details.value) {
      dietStore.updateIngredientQuantity({
        dayIndex: details.value.dayIndex,
        dishOrIngredientIndex: details.value.dishOrIngredientIndex,
        mealIndex: details.value.mealIndex,
        quantity,
      })
      refreshDishOrIngredient.value++
    }
  }
  const updateUsedPortions = (usedPortions: number) => {
    if (details.value) {
      dietStore.updateDishUsedPortions({
        dayIndex: details.value.dayIndex,
        dishOrIngredientIndex: details.value.dishOrIngredientIndex,
        mealIndex: details.value.mealIndex,
        usedPortions,
      })
      refreshDishOrIngredient.value++
    }
  }

  const updateMaxPortions = (maxPortions: number) => {
    if (details.value) {
      dietStore.updateDishMaxPortions({
        dayIndex: details.value.dayIndex,
        dishOrIngredientIndex: details.value.dishOrIngredientIndex,
        maxPortions,
        mealIndex: details.value.mealIndex,
      })
      refreshDishOrIngredient.value++
    }
  }

  const updatePreparationTime = (preparationTime: number) => {
    if (details.value) {
      dietStore.updateDishPreparationTime({
        dayIndex: details.value.dayIndex,
        dishOrIngredientIndex: details.value.dishOrIngredientIndex,
        mealIndex: details.value.mealIndex,
        preparationTime,
      })
    }
  }

  const updatePreparationSteps = (preparationSteps: string) => {
    if (details.value) {
      dietStore.updateDishPreparationSteps({
        dayIndex: details.value.dayIndex,
        dishOrIngredientIndex: details.value.dishOrIngredientIndex,
        mealIndex: details.value.mealIndex,
        preparationSteps,
      })
    }
  }

  const updateMealType = (mealType: StoreDietDish['meal_types'][number]) => {
    if (details.value) {
      dietStore.updateDishMealType({
        dayIndex: details.value.dayIndex,
        dishOrIngredientIndex: details.value.dishOrIngredientIndex,
        mealIndex: details.value.mealIndex,
        mealType,
      })
    }
  }

  const updateDishType = (dishType: StoreDietDish['dish_types'][number]) => {
    if (details.value) {
      dietStore.updateDishDishType({
        dayIndex: details.value.dayIndex,
        dishOrIngredientIndex: details.value.dishOrIngredientIndex,
        dishType,
        mealIndex: details.value.mealIndex,
      })
    }
  }
  const updateDishTypes = (dishTypesList: StoreDietDish['dish_types']) => {
    if (details.value) {
      dietStore.setDishDishTypes({
        dayIndex: details.value.dayIndex,
        dishOrIngredientIndex: details.value.dishOrIngredientIndex,
        dishTypesList,
        mealIndex: details.value.mealIndex,
      })
    }
  }
  const deleteDishOrIngredientHandler = () => {
    if (details.value) {
      dietStore.deleteDishOrIngredient(
        details.value.mealIndex,
        details.value.dayIndex,
        details.value.dishOrIngredientIndex,
      )
      dietStore.updateDetails()
    }
  }

  const closeDishOrIngredientHandler = () => {
    dietStore.updateDetails()
  }
  const showExtened = () => {
    dietStore.updateExtended(true)
  }
  const closeExtended = () => {
    dietStore.updateExtended(false)
  }
</script>

<style scoped>
  .dish-or-ingredient {
    position: relative;
    display: flex;
    flex-direction: column;
    max-height: calc(100% - 36px);
    padding: 16px;
    overflow: auto;
    background: #fff;
    box-shadow: 2px 4px 4px 0 hsl(0deg 0% 91% / 50%);
  }

  .menu {
    position: absolute;
    top: 21px;
    right: 16px;
    display: flex;
  }

  .menu-item {
    margin-left: 6px;
  }
</style>
